<template>
  <div class="wrap-edit-text">
    <perfect-scrollbar class="scrollBody">
      <div class="wrap-general-settings">
        <label class="general-title">General Setting</label>
        <div class="wrap-general-panel">
          <div class="wrap-general-form">
            <label class="form-item">Heading Font</label>
            <div class="form-item right-align">
              <GfDropdown
                :key="keyOpenFontManager"
                v-tooltip.top="{
                  visible: isDeactiveHeadingFont,
                  placement: 'top',
                  content: 'This font has been deactivated, you can activate in Font Manager or select another font.',
                  class: 'tooltip-warning-font',
                }"
                :is-scroll-dropdown="isScrollDropdown"
                :dropdown-styling-option="{ width: '164px' }"
                b-class="btn-dropdown-class"
                :value="generalSettingText.headingFont"
                dropdown-class="w-100"
                :options="getOptionFontManager(generalSettingText.headingFont, 'heading-font')"
                f-type="simple"
                @selected="selectedHeadingFont">
                <template #footer>
                  <div ref="heading-font" class="setting-fonts" @click="openFontManager('heading-font')">
                    Manage Fonts
                  </div>
                </template>
              </GfDropdown>
            </div>
          </div>
          <div class="wrap-general-form">
            <label class="form-item"> Paragraph Font</label>
            <div class="form-item right-align">
              <GfDropdown
                :key="keyOpenFontManager"
                v-tooltip.top="{
                  visible: isDeactiveParagraphFont,
                  placement: 'top',
                  content: 'This font has been deactivated, you can activate in Font Manager or select another font.',
                  class: 'tooltip-warning-font',
                }"
                :is-scroll-dropdown="isScrollDropdown"
                :dropdown-styling-option="{ width: '164px' }"
                b-class="btn-dropdown-class"
                :value="generalSettingText.paragraphFont"
                dropdown-class="w-100"
                :options="getOptionFontManager(generalSettingText.paragraphFont, 'paragraph-font')"
                f-type="simple"
                @selected="generalSettingText.paragraphFont = $event.value">
                <template #footer>
                  <div ref="paragraph-font" class="setting-fonts" @click="openFontManager('paragraph-font')">
                    Manage Fonts
                  </div>
                </template>
              </GfDropdown>
            </div>
          </div>
          <div class="wrap-general-form">
            <label class="form-item">Base size(px)</label>
            <div class="form-item right-align">
              <GfInputNumber
                :readonly="false"
                class="input-base-size"
                :min="1"
                :value="generalSettingText.baseSize"
                @onChange="generalSettingText.baseSize = $event.val" />
            </div>
          </div>
          <div class="wrap-general-form">
            <label class="form-item">Type Scale</label>
            <div class="form-item right-align">
              <GfDropdown
                :is-scroll-dropdown="isScrollDropdown"
                b-class="btn-dropdown-class"
                :value="generalSettingTextScale"
                dropdown-class="w-100"
                :options="textTypeScales"
                f-type="simple"
                @selected="selectedScale" />
            </div>
          </div>
        </div>
      </div>
      <div class="wrap-advanced-settings">
        <label class="advanced-title">Advanced Setting</label>
        <div class="adv-setting-wrap">
          <AdvancedTextItemSetting
            v-for="(item, index) in currentPresetOptions"
            :key="index"
            :texts="currentPresetOptions"
            :general-setting-text="generalSettingText"
            :selected-advanced-setting="selectedAdvancedSetting"
            :item="item"
            :index="index"
            :is-show-advance-setting="isShowAdvanceSetting"
            :advanced-setting="selectedAdvancedSetting"
            @on-tag-edit="onTagEdit"
            @on-tag-close="onTagClose"
            @click-away="onTagClose"
            @change-advanced-item="onChangTextItem" />
        </div>
      </div>
      <GuideCustomizeStyle
        v-if="!obGlobalStyle.tooltipTextStyle"
        title="Setup Text"
        guide="tooltipTextStyle"
        content="Text styles will be applied to your pages, you can adjust it later."
        link="https://help.gempages.net/articles/customize-global-style#table-content-1" />
    </perfect-scrollbar>
  </div>
</template>

<script lang="ts">
import GfDropdown from '@/core/components/base/GfDropdown.vue';
import GfInputNumber from '@/core/components/base/GfInputNumber.vue';
import { mapMutations, mapGetters, mapState, mapActions } from 'vuex';
import { mutations } from '@modules/editor-sidebar';
import { $gfLeftSideBar, $gryffBody, $jQuery } from '@/app/index';
import { presetOptionTypes, textTypeScales } from '../../constants/styleConstant';
import AdvancedTextItemSetting from './/AdvancedTextItemSetting.vue';
import emitter from '@/emitter';
import { addFontLink2Head, generateFontSizeWithTypeScale } from '@modules/global-style';
import { defineComponent } from 'vue';
import { cloneDeep } from '@/utils/common';
import GuideCustomizeStyle from '../guide/GuideCustomizeStyle.vue';

export default defineComponent({
  name: 'TextComponent',
  components: {
    GfDropdown,
    GfInputNumber,
    AdvancedTextItemSetting,
    GuideCustomizeStyle,
  },

  data: () => ({
    textTypeScales: textTypeScales,
    generalSettingText: {},
    preset: {},
    selectedAdvancedSetting: -1,
    keyOpenFontManager: 0,
    fontManagerClone: [],
    currentPresetOptions: [],
    isReadyToSave: true,
    isDeactiveHeadingFont: false,
    isDeactiveParagraphFont: false,
    isShowAdvanceSetting: false,
  }),
  computed: {
    ...mapState('sidebar', {
      currentPreset: (state) => (state as any).currentPreset,
      fontManager: (state) => (state as any).fontManager,
      styleIdTargetGlobalStyle: (state) => (state as any).styleIdTargetGlobalStyle,
    }),
    ...mapGetters('sidebar', {
      currentPresetOptionsStore: 'getCurrentPresetOptions',
    }),
    ...mapState('app', {
      shopMeta: 'shopMeta',
    }),
    ...mapGetters('font', {
      pageFonts: 'pageFonts',
    }),
    obGlobalStyle() {
      return this.shopMeta.onboardingGlobalStyle;
    },
    optionFontManager() {
      if (this.fontManagerClone && this.fontManagerClone.length > 0) {
        return this.fontManagerClone.map((font: any) => {
          font.name = font.styleName;
          font.value = font.googleName;
          return font;
        });
      }

      this.$nextTick(() => {
        return this.getFontManager();
      });

      return this.getFontManager();
    },
    generalSettingTextScale() {
      return this.generalSettingText.scale;
    },
    isScrollDropdown() {
      return true;
    },
  },
  watch: {
    generalSettingText: {
      deep: true,
      handler: function () {
        this.currentPresetOptions = this.currentPresetOptions.map((text: any) => {
          text.value.baseSize = this.generalSettingText.baseSize + 'px';
          text.value.headingFont = this.generalSettingText.headingFont;
          text.value.paragraphFont = this.generalSettingText.paragraphFont;
          text.value.scale = this.generalSettingText.scale;
          return text;
        });
      },
    },
    currentPresetOptionsStore(newV: any, _: any) {
      this.currentPresetOptions = newV;
      this.setGeneralSettingText();
    },
    styleIdTargetGlobalStyle(val) {
      const indexItem = this.currentPresetOptions.findIndex((item: any) => item.id == val);
      if (indexItem != -1) {
        this.onTagEdit(indexItem);
      }
    },
  },
  created() {
    emitter.on?.('changeFontManager', (fonts: any) => {
      this.fontManagerClone = fonts;
    });

    this.currentPresetOptions = cloneDeep(this.currentPreset.presetOptions.texts);
    this.setPresetTypeId(presetOptionTypes.text);
    this.makeCurrentPresetOptions();
    this.preset = this.currentPreset;
    this.setFontManager(window._pageFonts);
    this.setGeneralSettingText();
  },
  mounted() {
    this.setStyleIdTargetGS(parseInt(this.styleIdTargetGlobalStyle));
    const indexItem = this.currentPresetOptions.findIndex((item: any) => item.id == this.styleIdTargetGlobalStyle);
    this.selectedPack = indexItem;
  },
  methods: {
    ...mapMutations('sidebar', {
      setCurrentPreset: mutations.SET_CURRENT_PRESET,
      setFontManager: mutations.RELOAD_PAGE_FONTS,
      setPresetTypeId: mutations.SET_PRESET_TYPE_ID,
      setCurrentPresetOptions: mutations.SET_CURRENT_PRESET_OPTIONS,
      setStyleIdTargetGS: mutations.SET_STYLE_ID_TARGET_GLOBAL_STYLE,
    }),
    ...mapActions('app', {
      updateShopMeta: 'updateShopMeta',
    }),
    makeCurrentPresetOptions(): void {
      this.setCurrentPresetOptions(cloneDeep(this.currentPreset.presetOptions.texts));
    },
    setGeneralSettingText(): void {
      if (this.currentPresetOptions?.length) {
        this.generalSettingText = {
          baseSize: this.getValueWithDataUnit(this.currentPresetOptions[0].value.baseSize).value,
          headingFont: this.currentPresetOptions[0].value.headingFont,
          paragraphFont: this.currentPresetOptions[0].value.paragraphFont,
          scale: this.currentPresetOptions[0].value.scale,
        };
      }
    },
    selectedHeadingFont(font: any) {
      this.generalSettingText.headingFont = font.value;
      addFontLink2Head(font?.googleName);
    },
    selectedScale(option: any) {
      this.generalSettingText.scale = option.value;
      return generateFontSizeWithTypeScale(this.currentPresetOptions, option.value);
    },
    getFontManager() {
      if (!this.fontManager) return [];
      return this.fontManager.map((font: any) => {
        font.name = font.styleName;
        font.value = font.googleName;
        return font;
      });
    },
    getValueWithDataUnit(data: any) {
      const result = data.match(/[a-z]+|[^a-z]+/gi);
      return {
        value: parseFloat(result[0]),
        unit: result[1],
      };
    },
    openFontManager(type: string) {
      const ele = $jQuery(this.$refs[type]) as any;
      this.keyOpenFontManager++;
      ele.gfFontPicker({
        globalStyleSetting: true,
        onSelected: () => {},
      });
    },
    onChangTextItem(data: any) {
      this.currentPresetOptions[data.indexItem].value = data.text;
      if (data.changeFontSize) {
        this.generalSettingText['scale'] = 'custom';
      }
      this.setCurrentPreset(this.preset);
    },
    onTagEdit(i: number) {
      this.selectedAdvancedSetting = i;
      this.isShowAdvanceSetting = true;
      const _this = this;
      $gfLeftSideBar().data('gfleftsidebar').onBackgroundIframe('backgroundAdv');
      setTimeout(function () {
        $gryffBody()
          .find('.backgroundAdv')
          .off('mousedown.removeBackground')
          .on('mousedown.removeBackground', function () {
            _this.onTagClose();
          });
      }, 0);
    },
    onTagClose() {
      this.isShowAdvanceSetting = false;
      this.selectedAdvancedSetting = -1;
      $gfLeftSideBar().data('gfleftsidebar').offBackgroundIframe('backgroundAdv');
    },
    getOptionFontManager(selectedFont: string, type: string) {
      if (this.pageFonts && this.pageFonts.length > 0) {
        let fonts = [];
        let isDeactiveFont = true;
        fonts = this.pageFonts.map((font: any) => {
          if (selectedFont == font.styleName) {
            isDeactiveFont = false;
          }
          font.name = font.googleName;
          font.value = font.styleName;
          return font;
        });
        if (isDeactiveFont) {
          fonts.push({
            name: selectedFont,
            value: selectedFont,
            warning: true,
          });
        }
        type == 'heading-font'
          ? (this.isDeactiveHeadingFont = isDeactiveFont)
          : (this.isDeactiveParagraphFont = isDeactiveFont);
        return fonts;
      }
    },
  },
});
</script>

<style scoped lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.scrollBody {
  height: calc(100% - 125px);
}

.wrap-edit-text {
  color: map-get($text-dark-mode, 'low-contrast');
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  font-style: normal;
  height: 100%;

  .wrap-general-settings {
    margin-top: 16px;
    padding: 0 12px;

    .general-title {
      font-size: 11px;
      padding-bottom: 12px;
    }

    .wrap-general-panel {
      background: map-get($hybrid-shade-dark, '300');
      border-radius: $radius;
      -moz-border-radius: $radius;
      -webkit-border-radius: $radius;
      padding: 8px 8px 0 8px;

      .wrap-general-form {
        display: flex;
        padding-bottom: 8px;

        .form-item {
          flex: 0 0 50%;
          display: flex;
          align-items: center;
          padding-bottom: 0;
          max-width: 50%;

          :deep(.w-100) {
            width: 100%;
          }

          :deep(.btn-dropdown-class) {
            width: 100%;
            border: 1px solid map-get($hybrid-shade-dark, '200');
            background-color: inherit;

            .gf_dropdown__label {
              width: 100px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }

          .setting-fonts {
            color: map-get($primary-blue, '275');
            text-align: center;
            font-size: 12px;
            line-height: 14px;
            font-weight: 500;
            padding-bottom: 4px;
            padding-top: 8px;
            border-top: 1px solid map-get($text-dark-mode, 'disabled');
            cursor: pointer;
          }
        }

        .right-align {
          justify-content: flex-end;
        }
      }
    }
  }

  .wrap-advanced-settings {
    padding: 0 8px;

    .advanced-title {
      padding-left: 4px;
      padding-top: 16px;
      padding-bottom: 12px;
    }
  }
}
</style>
